import { useEffect, useState } from 'react';
import Layout from '../../components/Layout/Layout.component';
import { IPdfListItem, IPdfListResponse } from '../../services/supportPdf/supportPdf.types';
import { deletePdf, getPdfList } from '../../services/supportPdf/supportPdf.service';
import { AxiosResponse } from 'axios';
import { useStyles } from './SupportPdf.styles';
import { Prompt } from '../../components/Prompt/Prompt.component';
import Breadcrumbs from '../../components/Breadcrumbs.component';
import { Paths } from '../../utils/routerUrls';
import usePermission, { UserPermission } from '../../hooks/usePermission';
import { ButtonOne } from '../../components/ButtonOne/ButtonOne.component';
import { useTranslation } from 'react-i18next';
import { PdfDetails } from '../../components/SupportPdf/PdfDetails/PdfDetails.component';
import useDocumentTitle from '../../hooks/DocumentTitle/documentTitle';
import { PdfAdd } from '../../components/SupportPdf/PdfAdd/PdfAdd.component';
import { useLayoutContext } from '../../Context/Layout.context';
import { EditPdf } from '../../components/SupportPdf/EditPdf/EditPdf.component';
import { addPdf } from '../../const/addSupportPdf';
import { Grid, Typography } from '@material-ui/core';

export const SupportPdf: React.FC = () => {
  const [pdfs, setPdfs] = useState<IPdfListItem[]>([]);
  const [isAddPdfOpen, setIsAddPdfOpen] = useState<boolean>(false);
  const isAdminPermission = usePermission(UserPermission.AdminSupportManage);
  const [pdfDataToEdit, setPdfDataToEdit] = useState<IPdfListItem | undefined>(undefined);
  const [deletingPdfPromptData, setDeletingPdfPromptData] = useState<{ pdfId: string; title: string } | undefined>(
    undefined,
  );
  const { t } = useTranslation();
  useDocumentTitle("Zbiór instrukcji");
  const { openAlert } = useLayoutContext();
  const classes = useStyles();

  const refreshData = () =>
    getPdfList()
      .then((response: AxiosResponse<IPdfListResponse>) => {
        setPdfs(response.data.list);
      })
      .catch(() => {
        console.error('error');
        openAlert('get_error');
      });

  useEffect(() => {
    refreshData();
  }, []);

  const handleDeletePdf = (pdfId: string) => {
    deletePdf(pdfId)
      .then(
        (response: AxiosResponse<boolean>) => {
          if (response.data) {
            openAlert('success', 'Usuwanie pliku zostało pomyślnie zakończone');
            refreshData();
          } else {
            openAlert('error', `Usuwanie pliku nie powiodło się`);
          }
        },
        () => openAlert('error', `Usuwanie pliku nie powiodło się`),
      )
      .finally(() => {
        setDeletingPdfPromptData(undefined);
      });
  };

  return (
    <Layout viewType={isAdminPermission ? 'admin' : 'fullwidthForExaminer'}>
      <Grid className={classes.divContainer}>
        <div className={classes.divColumn}>
          <div>
            <Typography variant="h1" style={{ fontSize: '32px', fontWeight: 700, marginBottom: '20px' }}>
              {t('support:header.instructions')}
            </Typography>
          </div>
        </div>
        <div className={classes.divColumn}>
          <Breadcrumbs
            listRoute={Paths.supportPdf}
            currentList="Zbiór instrukcji"
            mainElementRoute={Paths.support}
            mainElement={`Centrum pomocy ${t('global:shortNames.beneficiary')} ${t('global:shortNames.system')}`}
          />
        </div>

        {isAdminPermission && (
          <div className={classes.buttonContainer}>
            <ButtonOne
              className={classes.buttonAddPdf}
              variant="contained"
              aria-label={addPdf + ' Pdf'}
              onClick={() => setIsAddPdfOpen(true)}
            >
              <span className={classes.spanAddPdf}>+</span> Dodaj instrukcję
            </ButtonOne>
          </div>
        )}

        {isAddPdfOpen && <PdfAdd onClose={() => setIsAddPdfOpen(false)} onChange={refreshData} />}

        {pdfDataToEdit && (
          <EditPdf onClose={() => setPdfDataToEdit(undefined)} onChange={refreshData} pdfProp={pdfDataToEdit} />
        )}

        {deletingPdfPromptData && (
          <Prompt
            type="confirmation"
            open={true}
            size="xs"
            onConfirm={() => handleDeletePdf(deletingPdfPromptData.pdfId)}
            onCancel={() => setDeletingPdfPromptData(undefined)}
            mainActionButtonText="TAK"
            mainText="Czy na pewno chcesz usunąć instrukcję:"
            subTexts={[{ text: deletingPdfPromptData.title }]}
          />
        )}
        <div className={classes.divSeconderyContainer}>
          {pdfs &&
            pdfs.map((pdf) => (
              <PdfDetails
                key={pdf.id}
                pdfProp={pdf}
                deleteFunc={() => setDeletingPdfPromptData({ pdfId: pdf.id, title: pdf.title })}
                updateFunc={() => setPdfDataToEdit(pdf)}
                isAdminPermission={isAdminPermission}
              ></PdfDetails>
            ))}
        </div>
        {pdfs.length === 0 && <h2 className={classes.noAvailableFilesHeader}>Brak dostępnych instrukcji.</h2>}
      </Grid>
    </Layout>
  );
};
